import React  from 'react';
import _ from 'lodash';
import moment from 'moment-timezone';
import { useConnect } from '@lore/query-connect';
import { PayloadStates } from '@lore/utils';
import ClampableText from '@/components/ClampableText';
import Avatar from '@/components/Avatar';
import logo from '@communityshare/ui-shared/images/logo.png';
import { getDistanceFromLatLonInMiles } from '@/components/User/utils';
import classNames from '@/utils/classNames';
import useTags from '@/hooks/useTags';
import TagTypes from '@/constants/TagTypes';
import useOfferTags from '@/hooks/useOfferTags';

export default function useOfferEmailPreview(offer, educator, customMessage, currentUser, network) {
  const partner = useConnect('user.byId', {
    id: offer.data.creatorId
  });
  const distance = getDistanceFromLatLonInMiles(educator.data, partner.data);

  const activity = useConnect('tag.byId', {
    id: offer.data.activityId
  });

  const times = useTags(TagTypes.Time);
  const offerTimes = useOfferTags(offer, TagTypes.Time);

  const offerInteractionTypes = useOfferTags(offer, TagTypes.InteractionType);

  if (
    activity.state === PayloadStates.FETCHING ||
    times.state === PayloadStates.FETCHING ||
    offerTimes.state === PayloadStates.FETCHING ||
    offerInteractionTypes.state === PayloadStates.FETCHING
  ) {
    return '...';
  }

  function getActivity() {
    return {
      title: `${activity.data.name}`,
      text: `${activity.data.description}`
    };
  }

  function getSummary() {
    return {
      title: `${offer.data.title || activity.data.name}`,
      text: `${offer.data.description || activity.data.description}`
    };
  }

  function getDate() {
    if (
      offer.data.specificDate === 'Yes' &&
      offer.data.date
    ) {
      return {
        title: `Date`,
        text: `${moment(offer.data.date).format('MMM Do, YYYY')}`
      };
    }

    if (
      offer.data.specificDate === 'No' &&
      offer.data.startDate &&
      offer.data.endDate
    ) {
      return {
        title: `Date Window`,
        text: `${moment(offer.data.startDate).format('MMM Do, YYYY')} - ${moment(offer.data.endDate).format('MMM Do, YYYY')}`
      };
    }

    return {
      title: `Date`,
      text: `(not provided)`
    };
  }

  function getTime() {
    if (
      offer.data.specificTime === 'Yes' &&
      offer.data.startTime &&
      offer.data.endTime
    ) {
      const startTime = moment(offer.data.startTime).tz(partner.data.timezone).format('h:mm a');
      const endTime = moment(offer.data.endTime).tz(partner.data.timezone).format('h:mm a');
      const timezone = moment().tz(partner.data.timezone).format('z');

      return {
        title: `Time`,
        text: `${startTime} - ${endTime} ${timezone}`
      };
    }

    if (offerTimes.data.length > 0) {
      return {
        title: `Possible Times`,
        text: offerTimes.data.map(function(offerTime) {
          return offerTime.data.name;
        }).join('\n')
      };
    }

    return {
      title: `Time`,
      text: `(not provided)`
    };
  }

  function getInPerson() {
    return !!_.find(offerInteractionTypes.data, function(offerInteractionType) {
      return offerInteractionType.data.name === 'In-person';
    });
  }

  function getVirtual() {
    return !!_.find(offerInteractionTypes.data, function(offerInteractionType) {
      return offerInteractionType.data.name === 'Virtual';
    });
  }

  function getCost() {
    return {
      title: `Cost`,
      text: offer.data.hasCost === 'Yes' ?
        offer.data.cost || '(not provided)' :
        'Free'
    };
  }

  const data = {
    activity: getActivity(),
    summary: getSummary(),
    date: getDate(),
    time: getTime(),
    inPerson: getInPerson(),
    virtual: getVirtual(),
    cost: getCost()
  };

  return (
    <div className="bg-cs-gray-100 p-6">
      <div className="h-1 bg-accent w-full mb-8"/>
      <div className="p-6 mb-8">
        <img
          className="w-24 mx-auto"
          src={logo}
          alt="logo"
        />
      </div>
      <div className="mb-8">
        <div className="text-base text-gray-500">
          Hi {educator.data.firstName},
        </div>
        <div className="my-3 text-base text-gray-500">
          <ClampableText markup>
            {customMessage}
          </ClampableText>
        </div>
        <div className="mb-3">
          <div className="text-base text-gray-500">
            Cheers,
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <div>
            <Avatar size={12} src={currentUser.data.avatarUrl} />
          </div>
          <div>
            <div className="text-lg text-gray-700 font-semibold">
              {currentUser.data.firstName} {currentUser.data.lastName}
            </div>
            <div className="text-sm text-gray-500">
              Steward for {currentUser.data.isStaff ? 'CommunityShare' : network?.data.name || 'CommunityShare'}
            </div>
          </div>
        </div>
      </div>
      <div className="mb-8">
        <div className="flex flex-col text-center justify-center bg-accent rounded-xl p-6">
          <div className="text-2xl text-white font-bold mb-2">
            Are you interested in this Offer?
          </div>
          <div className="text-base text-white mb-4">
            That&apos;s wonderful! Connect with {partner.data.firstName} to participate, ask
            questions, offer feedback, or just to refer someone you think might be a good fit.
          </div>
          <div className="bg-white rounded-lg w-56 text-center py-3 m-auto">
            <div className="text-base text-accent uppercase font-bold">
              Respond or Decline
            </div>
          </div>
        </div>
      </div>
      <div className="mb-8 space-y-4">
        <div className="text-2xl font-bold">
          Activity
        </div>
        <div className="bg-white rounded-lg p-6">
          <div className="text-xl font-semibold">
            {data.activity.title}
          </div>
          <div className="text-gray-500">
            {data.activity.text}
          </div>
        </div>
        <div className="text-2xl font-bold">
          Details
        </div>
        <div className="bg-white rounded-lg p-6 space-y-8">
          <div className="text-2xl font-semibold">
            {data.summary.title}
          </div>
          <div>
            <div className="text-xl font-semibold">
              Summary
            </div>
            <div className="text-base text-gray-500">
              <ClampableText markup>
                {data.summary.text}
              </ClampableText>
            </div>
          </div>
          <div>
            <div className="text-xl font-semibold">
              {data.date.title}
            </div>
            <div className="text-gray-500">
              <ClampableText markup>
                {data.date.text}
              </ClampableText>
            </div>
          </div>
          <div>
            <div className="text-xl font-semibold">
              {data.time.title}
            </div>
            <div className="text-gray-500">
              <ClampableText markup>
                {data.time.text}
              </ClampableText>
            </div>
          </div>
          <div>
            <div className="text-xl font-semibold">
              Collaboration Options
            </div>
            {!data.virtual && !data.inPerson && (
              <div>
                <div className="text-gray-700">
                  (not provided)
                </div>
                <div className="text-xs text-gray-500">
                  {distance <= 1 ? '1 mile away' : `${distance} miles away`}
                </div>
              </div>
            )}
            {data.virtual && (
              <div>
                <div className={classNames(
                  'text-gray-700',
                  data.inPerson && 'mb-1'
                )}>
                  Virtual
                </div>
              </div>
            )}
            {data.inPerson && (
              <div>
                <div className="text-gray-700">
                  In-person
                </div>
                <div className="text-xs text-gray-500">
                  {distance <= 1 ? '1 mile away' : `${distance} miles away`}
                </div>
              </div>
            )}
          </div>
          <div>
            <div className="text-xl font-semibold">
              {data.cost.title}
            </div>
            <div className="text-gray-500">
              {data.cost.text}
            </div>
          </div>
        </div>
      </div>
      <div className="mb-8">
        <div className="flex flex-col text-center justify-center bg-accent rounded-xl p-6">
          <div className="text-2xl text-white font-bold mb-2">
            Are you interested in this Offer?
          </div>
          <div className="text-base text-white mb-4">
            That&apos;s wonderful! Connect with {partner.data.firstName} to participate, ask
            questions, offer feedback, or just to refer someone you think might be a good fit.
          </div>
          <div className="bg-white rounded-lg w-56 text-center py-3 m-auto">
            <div className="text-base text-accent uppercase font-bold">
              Respond or Decline
            </div>
          </div>
        </div>
      </div>
      <div className="h-1 bg-accent w-full"/>
    </div>
  );
};
